import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart3, Loader2, Search } from "lucide-react";

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "—");
const tone = (pct) => (pct == null ? "bg-slate-300" : pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500");

// Iter 133: Dashboard commesse — avanzamento ore vs budget, per tutti gli utenti (flag can_view_dashboard_commesse)
export default function DashboardCommessePage() {
  const { user } = useAuth();
  const [mesi, setMesi] = useState(() => Number(localStorage.getItem("dash_commesse_mesi")) || 3);
  const [q, setQ] = useState("");
  const [soloBudget, setSoloBudget] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState(() => localStorage.getItem("dash_commesse_tipo") || "tutte"); // tutte | cantiere | officina
  const setTipoPersist = (t) => { setTipoFiltro(t); localStorage.setItem("dash_commesse_tipo", t); };
  const [gruppo, setGruppo] = useState(() => { const g = localStorage.getItem("dash_commesse_gruppo"); return g === "cliente" ? "cliente" : "nessuno"; }); // nessuno | cliente
  const [sel, setSel] = useState([]); // Iter 137: commesse flaggate per la somma libera
  const toggleSel = (c) => setSel((a) => (a.includes(c) ? a.filter((x) => x !== c) : [...a, c]));
  const [aperti, setAperti] = useState({});
  const setGruppoPersist = (g) => { setGruppo(g); localStorage.setItem("dash_commesse_gruppo", g); };
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m = mesi) => {
    setLoading(true);
    try {
      const { data } = await api.get("/commesse/dashboard", { params: { mesi: m } });
      setRes(data);
      localStorage.setItem("dash_commesse_mesi", String(m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (user && user.can_view_dashboard_commesse === false) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="dash-commesse-disabled">La Dashboard commesse non è abilitata per questo account.</div>;
  }
  const isOff = (i) => i.codice.endsWith("-OFF");
  const items = (res?.items || []).filter((i) => (tipoFiltro === "tutte" || (tipoFiltro === "officina") === isOff(i)) && (!soloBudget || i.budget_ore) && (!q || `${i.codice} ${i.descrizione} ${i.cliente} ${i.sede || ""}`.toLowerCase().includes(q.toLowerCase())));
  // Iter 135: raggruppamento per cliente o per sede/stabilimento con budget e ore sommati
  const gruppi = (() => {
    if (gruppo === "nessuno") return null;
    const map = new Map();
    for (const i of items) {
      const k = (gruppo === "cliente" ? i.cliente : i.sede) || (gruppo === "cliente" ? "Senza cliente" : "Senza sede collegata");
      if (!map.has(k)) map.set(k, { key: k, items: [], ore_tot: 0, ore_periodo: 0, budget: 0, con_budget: 0 });
      const g = map.get(k);
      g.items.push(i); g.ore_tot += i.ore_tot; g.ore_periodo += i.ore_periodo;
      if (i.budget_ore) { g.budget += i.budget_ore; g.con_budget += 1; }
    }
    return [...map.values()].map((g) => ({ ...g, ore_tot: Math.round(g.ore_tot * 10) / 10, ore_periodo: Math.round(g.ore_periodo * 10) / 10,
      residuo: g.budget ? Math.round((g.budget - g.items.filter((i) => i.budget_ore).reduce((a, i) => a + i.ore_tot, 0)) * 10) / 10 : null,
      pct: g.budget ? Math.round(g.items.filter((i) => i.budget_ore).reduce((a, i) => a + i.ore_tot, 0) / g.budget * 1000) / 10 : null }))
      .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || b.ore_periodo - a.ore_periodo);
  })();
  const Riga = ({ i }) => (
    <div key={i.codice} className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`dash-commessa-${i.codice}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0 flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4 shrink-0" checked={sel.includes(i.codice)} onChange={() => toggleSel(i.codice)} title="Aggiungi alla somma" data-testid={`dash-sel-${i.codice}`} />
          <span className="font-mono font-bold text-[#0044FF]">{i.codice}</span>
          <span className="ml-2 font-semibold">{i.descrizione}</span>
          {i.cliente && <span className="ml-2 text-xs text-slate-500">· {i.cliente}</span>}
          {i.sede && <span className="ml-2 text-xs text-indigo-700">· {i.sede}</span>}
          <span className={`ml-2 text-[10px] font-bold rounded px-1 ${isOff(i) ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{isOff(i) ? "OFFICINA" : "CANTIERE"}</span>
          {i.attiva === false && <span className="ml-2 text-[10px] font-bold bg-slate-200 text-slate-600 rounded px-1">CHIUSA</span>}
        </div>
        <div className="text-sm"><b>{i.ore_tot} h</b> totali<span className="text-slate-500"> · {i.ore_periodo} h nel periodo · dal {fmtD(i.prima_data)} al {fmtD(i.ultima_data)}</span></div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${tone(i.pct)} transition-[width]`} style={{ width: `${Math.min(100, i.pct ?? 0)}%` }} /></div>
        <div className="text-sm w-[260px] text-right shrink-0" data-testid={`dash-commessa-budget-${i.codice}`}>
          {i.budget_ore ? (
            <span className={i.pct >= 100 ? "text-red-700 font-bold" : i.pct >= 80 ? "text-amber-800 font-bold" : "text-emerald-800"}>{i.pct}% · budget {i.budget_ore} h · {i.residuo >= 0 ? `restano ${i.residuo} h` : `superato di ${Math.abs(i.residuo)} h`}</span>
          ) : <span className="text-slate-400">nessun budget impostato</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-5" data-testid="dash-commesse-page">
      <div>
        <h1 className="text-3xl font-heading font-black tracking-tight flex items-center gap-2"><BarChart3 className="h-7 w-7 text-[#0044FF]" /> Dashboard commesse</h1>
        <p className="text-sm text-slate-500 mt-1">Commesse con ore registrate negli ultimi <b>{mesi}</b> mesi. Le ore mostrate sono <b>tutte</b> le ore della commessa (anche precedenti), confrontate con il budget. Spunta le commesse per sommarne budget e ore lavorate.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex gap-1">
          {[1, 3, 6, 12].map((m) => (
            <Button key={m} size="sm" variant={mesi === m ? "default" : "outline"} onClick={() => { setMesi(m); load(m); }} data-testid={`dash-commesse-mesi-${m}`}>{m} {m === 1 ? "mese" : "mesi"}</Button>
          ))}
        </div>
        <label className="text-sm flex items-center gap-1">altro: <Input type="number" min={1} max={60} value={mesi} onChange={(e) => setMesi(Number(e.target.value) || 1)} onBlur={() => load(mesi)} className="h-9 w-[70px]" data-testid="dash-commesse-mesi-input" /> mesi</label>
        <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca commessa, cliente…" className="h-9 pl-7 w-[240px]" data-testid="dash-commesse-search" /></div>
        <label className="text-sm flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={soloBudget} onChange={(e) => setSoloBudget(e.target.checked)} data-testid="dash-commesse-solo-budget" /> solo con budget</label>
        <div className="flex items-center gap-1 text-sm" data-testid="dash-commesse-tipo"><span className="text-slate-500">Tipo:</span>
          {[["tutte", "Tutte"], ["cantiere", "Solo cantiere"], ["officina", "Solo officina (-OFF)"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={tipoFiltro === k ? "default" : "outline"} onClick={() => setTipoPersist(k)} data-testid={`dash-commesse-tipo-${k}`}>{l}</Button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-sm"><span className="text-slate-500">Raggruppa:</span>
          {[["nessuno", "No"], ["cliente", "Cliente"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={gruppo === k ? "default" : "outline"} onClick={() => setGruppoPersist(k)} data-testid={`dash-commesse-gruppo-${k}`}>{l}</Button>
          ))}
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {res && (
        <div className="flex flex-wrap gap-2 text-sm" data-testid="dash-commesse-riepilogo">
          <span className="rounded-lg border bg-white px-3 py-1.5"><b>{res.totale}</b> commesse attive nel periodo</span>
          <span className="rounded-lg border bg-white px-3 py-1.5"><b>{items.filter((i) => !isOff(i)).length}</b> cantiere · <b>{items.filter(isOff).length}</b> officina</span>
          <span className="rounded-lg border bg-white px-3 py-1.5"><b>{res.con_budget}</b> con budget</span>
          <span className={`rounded-lg border px-3 py-1.5 ${res.oltre_80 ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-white"}`}><b>{res.oltre_80}</b> oltre l'80% del budget</span>
        </div>
      )}

      {sel.length > 0 && (() => {
        const rows = (res?.items || []).filter((i) => sel.includes(i.codice));
        const conB = rows.filter((i) => i.budget_ore);
        const budget = Math.round(conB.reduce((a, i) => a + i.budget_ore, 0) * 10) / 10;
        const lavB = conB.reduce((a, i) => a + i.ore_tot, 0);
        const tot = Math.round(rows.reduce((a, i) => a + i.ore_tot, 0) * 10) / 10;
        const per = Math.round(rows.reduce((a, i) => a + i.ore_periodo, 0) * 10) / 10;
        const pct = budget ? Math.round(lavB / budget * 1000) / 10 : null;
        const residuo = budget ? Math.round((budget - lavB) * 10) / 10 : null;
        return (
          <div className="sticky top-16 z-10 rounded-xl border-2 border-[#0044FF] bg-blue-50 p-3 space-y-1 shadow" data-testid="dash-selezione">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="font-heading font-bold text-[#0044FF]">Somma di {rows.length} commesse selezionate</div>
              <div className="text-sm"><b>{per} h</b> lavorate nel periodo <span className="text-slate-500">· {tot} h totali</span></div>
              <Button size="sm" variant="outline" onClick={() => setSel([])} data-testid="dash-selezione-azzera">Azzera</Button>
            </div>
            <div className="text-[11px] text-slate-600 font-mono">({rows.map((i) => i.codice).join(", ")})</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 rounded-full bg-white overflow-hidden border"><div className={`h-full ${tone(pct)}`} style={{ width: `${Math.min(100, pct ?? 0)}%` }} /></div>
              <div className="text-sm text-right shrink-0" data-testid="dash-selezione-budget">
                {budget ? <span className={pct >= 100 ? "text-red-700 font-bold" : pct >= 80 ? "text-amber-800 font-bold" : "text-emerald-800"}>{pct}% · budget {budget} h ({conB.map((i) => i.codice).join(", ")}) · {residuo >= 0 ? `restano ${residuo} h` : `superato di ${Math.abs(residuo)} h`}</span> : <span className="text-slate-400">nessun budget tra le selezionate</span>}
              </div>
            </div>
          </div>
        );
      })()}
      <div className="space-y-2" data-testid="dash-commesse-lista">
        {!gruppi && items.map((i) => <Riga key={i.codice} i={i} />)}
        {gruppi && gruppi.map((g) => {
          const open = aperti[g.key] !== false;
          return (
            <div key={g.key} className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid={`dash-gruppo-${g.key}`}>
              <button type="button" onClick={() => setAperti((a) => ({ ...a, [g.key]: !open }))} className="w-full text-left" data-testid={`dash-gruppo-toggle-${g.key}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <div className="font-heading font-bold text-indigo-900">{open ? "▾" : "▸"} {g.key} <span className="text-xs font-normal text-slate-500">· {g.items.length} commesse</span>
                    {(() => { const off = g.items.filter(isOff).length; const cant = g.items.length - off;
                      const lab = off && cant ? `cantiere ${cant} + officina ${off}` : off ? "solo officina" : "solo cantiere";
                      return <span className={`ml-2 text-[10px] font-bold rounded px-1.5 py-0.5 ${off && cant ? "bg-violet-100 text-violet-800" : off ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`dash-gruppo-tipo-${g.key}`}>{lab}</span>; })()}
                  </div>
                  <div className="text-sm"><b>{g.ore_periodo} h</b> lavorate nel periodo <span className="text-slate-500">· {g.ore_tot} h totali</span></div>
                </div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5" data-testid={`dash-gruppo-codici-${g.key}`}>({g.items.map((i) => i.codice).join(", ")})</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1 h-3 rounded-full bg-white overflow-hidden border"><div className={`h-full ${tone(g.pct)}`} style={{ width: `${Math.min(100, g.pct ?? 0)}%` }} /></div>
                  <div className="text-sm w-[300px] text-right shrink-0" data-testid={`dash-gruppo-budget-${g.key}`}>
                    {g.budget ? (
                      <span className={g.pct >= 100 ? "text-red-700 font-bold" : g.pct >= 80 ? "text-amber-800 font-bold" : "text-emerald-800"}>{g.pct}% · budget {Math.round(g.budget * 10) / 10} h ({g.items.filter((i) => i.budget_ore).map((i) => i.codice).join(", ")}) · {g.residuo >= 0 ? `restano ${g.residuo} h` : `superato di ${Math.abs(g.residuo)} h`}</span>
                    ) : <span className="text-slate-400">nessun budget nel gruppo</span>}
                  </div>
                </div>
              </button>
              {open && <div className="space-y-2 pl-2">{g.items.map((i) => <Riga key={i.codice} i={i} />)}</div>}
            </div>
          );
        })}
        {res && items.length === 0 && <div className="text-sm text-slate-500 p-6 text-center">Nessuna commessa con ore nel periodo.</div>}
      </div>
    </div>
  );
}
